"use client";

import { LOCAL_STORAGE_KEYS } from "@/src/config/constant";
import log from "logbeacon";

const QUIZ_RESULT_CONTEXT_TTL_MS = 24 * 60 * 60 * 1000;

type QuizResultContext = {
  resultId: string;
  updatedAt: number;
};

const isBrowser = () => typeof window !== "undefined";

const parseContext = (raw: string | null): QuizResultContext | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as QuizResultContext;
    if (!parsed?.resultId || typeof parsed.resultId !== "string") {
      return null;
    }
    if (!Number.isFinite(parsed.updatedAt)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const getQuizResultContext = (
  now = Date.now(),
): QuizResultContext | null => {
  if (!isBrowser()) {
    return null;
  }
  const context = parseContext(
    localStorage.getItem(LOCAL_STORAGE_KEYS.ONBOARDING_QUIZ_RESULT_CONTEXT),
  );
  if (!context) {
    return null;
  }
  if (now - context.updatedAt > QUIZ_RESULT_CONTEXT_TTL_MS) {
    log.debug("[resultId-flow] context expired, clearing");
    clearQuizResultContext("expired");
    return null;
  }
  return context;
};

export const getQuizResultId = (): string | null => {
  return getQuizResultContext()?.resultId ?? null;
};

export const upsertQuizResultId = (resultId: string): void => {
  if (!isBrowser()) {
    return;
  }
  const normalizedResultId = resultId.trim();
  if (!normalizedResultId) {
    return;
  }
  const prev = getQuizResultId();
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.ONBOARDING_QUIZ_RESULT_CONTEXT,
    JSON.stringify({
      resultId: normalizedResultId,
      updatedAt: Date.now(),
    } satisfies QuizResultContext),
  );
  if (prev && prev !== normalizedResultId) {
    log.debug(
      `[resultId-flow] overwritten old=${prev} new=${normalizedResultId} source=route`,
    );
  } else {
    log.debug(`[resultId-flow] captured resultId=${normalizedResultId}`);
  }
};

export const clearQuizResultContext = (reason: string): void => {
  if (!isBrowser()) {
    return;
  }
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ONBOARDING_QUIZ_RESULT_CONTEXT);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.ONBOARDING_QUIZ_RESULT_CACHE);
  log.debug(`[resultId-flow] context cleared reason=${reason}`);
};

export const cacheQuizResultPayload = (payload: unknown): void => {
  if (!isBrowser()) {
    return;
  }
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.ONBOARDING_QUIZ_RESULT_CACHE,
    JSON.stringify(payload),
  );
};
